import { useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { PDFExport } from "@progress/kendo-react-pdf";
import EmptyState from "../components/common/EmptyState";
import ExpenseFilters from "../components/expenses/ExpenseFilters";
import ExpenseMobileCards from "../components/expenses/ExpenseMobileCards";
import ExpenseTable from "../components/expenses/ExpenseTable";
import ExpenseToolbar from "../components/expenses/ExpenseToolbar";
import { emptyFilters } from "../constants";

const hasFilters = (filters) =>
  filters.search ||
  filters.category !== emptyFilters.category ||
  filters.priority !== emptyFilters.priority ||
  filters.period !== emptyFilters.period ||
  filters.tags.length ||
  filters.minAmount != null ||
  filters.maxAmount != null ||
  filters.startDate ||
  filters.endDate;

export default function ExpensesPage({
  currencyCode,
  expenses,
  filters,
  locale,
  onAdd,
  onDelete,
  onEdit,
  onExportCsv,
  onExportJson,
  onImportCsv,
  onImportFile,
  onResetAll,
  onUpdateFilters,
  t = {}
}) {
  const excelRef = useRef(null);
  const pdfRef = useRef(null);
  const hasActiveFilters = hasFilters(filters);

  return (
    <div className="page-stack">
      <ExpenseToolbar
        excelRef={excelRef}
        onAdd={onAdd}
        onExportCsv={onExportCsv}
        onExportJson={onExportJson}
        onImportCsv={onImportCsv}
        onImportFile={onImportFile}
        pdfRef={pdfRef}
        t={t}
      />

      <ExpenseFilters filters={filters} locale={locale} onUpdateFilters={onUpdateFilters} t={t} />

      <ExcelExport ref={excelRef} data={expenses}>
        <PDFExport ref={pdfRef} paperSize="A4" margin="1cm">
          <section className="panel table-panel">
            {expenses.length ? (
              <>
                <ExpenseTable
                  currencyCode={currencyCode}
                  expenses={expenses}
                  locale={locale}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  t={t}
                />
                <ExpenseMobileCards
                  currencyCode={currencyCode}
                  expenses={expenses}
                  locale={locale}
                  onDelete={onDelete}
                  onEdit={onEdit}
                />
              </>
            ) : (
              <EmptyState
                actionLabel={hasActiveFilters ? t.clearFilters || "Clear filters" : t.addFirstExpense || "Add first expense"}
                description={
                  hasActiveFilters
                    ? t.noMatches || "Nothing matches these filters yet."
                    : t.firstExpenseHint || "Add your first transaction and LedgerFlow will start building analytics."
                }
                icon="k-i-dollar"
                onAction={hasActiveFilters ? () => onUpdateFilters(emptyFilters) : onAdd}
                title={hasActiveFilters ? t.noExpensesFound || "No expenses found" : t.noExpensesYet || "No expenses yet"}
              />
            )}
          </section>
        </PDFExport>
      </ExcelExport>

      <div className="danger-row">
        <span>{t.needCleanSlate || "Need a clean slate?"}</span>
        <Button fillMode="flat" themeColor="error" icon="reset" onClick={onResetAll}>
          {t.resetAllLocalData || "Reset all local data"}
        </Button>
      </div>
    </div>
  );
}
